import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useDropzone } from 'react-dropzone';
import WayfinderLocationTemplate from './WayfinderLocationTemplate';
import UseToast from '../UseToast';
import useS3Upload from '../../hooks/useS3Upload';
import useCurrentLanguage from '../../hooks/useCurrentLanguage';

const WayfinderSubLocation = () => {
  const navigate = useNavigate();
  const { userId, subLocationId } = useParams();
  const { showErrorToast, showSuccessToast } = UseToast();

  const [loading, setLoading] = useState(false);
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [buttonColor, setButtonColor] = useState('#000000');
  const { currentLanguage } = useCurrentLanguage(userId);

  // File states for uploads
  const [photoFile, setPhotoFile] = useState(null);
  const [avatarVideoFile, setAvatarVideoFile] = useState(null);
  const [mapVideoFile, setMapVideoFile] = useState(null);
  const [logoBeforeMapFile, setLogoBeforeMapFile] = useState(null);
  const [qrCodeFile, setQrCodeFile] = useState(null);
  const [clickSoundFile, setClickSoundFile] = useState(null);
  const [pngFile, setPngFile] = useState(null);
  const [btnIcon, setBtnIcon] = useState(null);

  // Corner logo files
  const [upperLeftLogoFile, setUpperLeftLogoFile] = useState(null);
  const [upperRightLogoFile, setUpperRightLogoFile] = useState(null);
  const [bottomLeftLogoFile, setBottomLeftLogoFile] = useState(null);
  const [bottomRightLogoFile, setBottomRightLogoFile] = useState(null);
  const [backButtonFile, setBackButtonFile] = useState(null);

  // Uploading states
  const [uploadingStates, setUploadingStates] = useState({
    upperLeft: false,
    upperRight: false,
    bottomLeft: false,
    bottomRight: false,
    backButton: false,
    locationPhoto: false,
  });

  // S3 Upload hook
  const { uploadFile: uploadToS3, uploading: s3Uploading } = useS3Upload({
    signedUrlEndpoint: '/generate-signed-url',
    onSuccess: (fileUrl, file) => {
      showSuccessToast(`${file.name} uploaded successfully`);
    },
    onError: () => {
      showErrorToast('Upload failed');
    },
  });

  // Initial state
  const [item, setItem] = useState({
    name: '',
    description: '',
    status: true,
    enabled: true,
    websiteUrl: '',
    airportUrl: '',
    textDirections: '',
    avatar_api_key_id: '',
    avatar_key_id: '',

    avatarVideo: {
      fileUrl: '',
      fileName: '',
      enabled: false,
    },

    qrCode: {
      fileUrl: '',
      fileName: '',
      enabled: false,
      width: 100,
      height: 100,
    },

    operationHours: [
      { day: 'monday', open: '09:00', close: '17:00', isOpen: true, isFullDay: false },
      { day: 'tuesday', open: '09:00', close: '17:00', isOpen: true, isFullDay: false },
      { day: 'wednesday', open: '09:00', close: '17:00', isOpen: true, isFullDay: false },
      { day: 'thursday', open: '09:00', close: '17:00', isOpen: true, isFullDay: false },
      { day: 'friday', open: '09:00', close: '17:00', isOpen: true, isFullDay: false },
      { day: 'saturday', open: '10:00', close: '16:00', isOpen: true, isFullDay: false },
      { day: 'sunday', open: '10:00', close: '16:00', isOpen: true, isFullDay: false },
    ],

    globalSettings: {
      cornerRadius: 0,
      height: 50,
      width: 150,
      outlineColor: '#000000',
      outlineThickness: 1,
      fillColor: '#ffffff',
      font: 'Arial',
      fontColor: '#127E10',
      fontSize: 16,
      textAlign: 'center',
      marginBottom: 2,
      lineHeight: 1,
      padding: 10,
      shadow: {
        angle: 5,
        size: 10,
        distance: 5,
        alpha: 0.5,
        color: '#FF5733',
      },
      highlight: {
        borderColor: '#000000',
        fillColor: '#ffffff',
        fontColor: '#000000',
      },
      clickSound: '',
      clickSoundFilename: null,
      clickSoundVolume: 1,
      pngBtnenabled: false,
      hideBtnText: false,
      btnPng: null,
      btnPngFileName: null,
    },

    customSettings: {
      cornerRadius: 0,
      height: 50,
      width: 150,
      outlineColor: '#000000',
      outlineThickness: 1,
      fillColor: '#ffffff',
      font: 'Arial',
      fontColor: '#127E10',
      fontSize: 16,
      textAlign: 'center',
      marginBottom: 2,
      lineHeight: 1,
      padding: 10,
      shadow: {
        angle: 5,
        size: 10,
        distance: 5,
        alpha: 0.5,
        color: '#FF5733',
      },
      highlight: {
        borderColor: '#000000',
        fillColor: '#ffffff',
        fontColor: '#000000',
      },
      clickSound: '',
      clickSoundFilename: null,
      clickSoundVolume: 1,
      pngBtnenabled: false,
      hideBtnText: false,
      btnPng: null,
      btnPngFileName: null,
    },

    uploadedUrls: {
      upperLeft: { fileUrl: '', fileName: '', enabled: false, width: 100, height: 100 },
      upperRight: { fileUrl: '', fileName: '', enabled: false, width: 100, height: 100 },
      bottomLeft: { fileUrl: '', fileName: '', enabled: false, width: 100, height: 100 },
      bottomRight: { fileUrl: '', fileName: '', enabled: false, width: 100, height: 100 },
      backButton: { fileUrl: '', fileName: '', enabled: false, width: 100, height: 100 },
      locationPhoto: { fileUrl: '', fileName: '', enabled: false, width: 100, height: 100 },
    },

    parentCategories: [],
    parentSubcategories: [],
    parentLocationId: null,
  });

  useEffect(() => {
    if (userId && subLocationId) {
      fetchSubLocationData();
      fetchSiteBrandColor();
    }
  }, [userId, subLocationId]);

  const fetchSiteBrandColor = async () => {
    try {
      const res = await axios.get(`/auth/aiform/all/${userId}`);
      setButtonColor(res.data?.brand_color || '#000000');
    } catch (err) {
      console.error('Failed to fetch brand color:', err);
    }
  };

  const fetchSubLocationData = async () => {
    if (!subLocationId || !userId) return;

    try {
      setLoading(true);
      const res = await axios.get(`/api/wayfinder/sublocations/${userId}`);

      if (!res.data?.success) {
        showErrorToast('No sublocation data found');
        return;
      }

      const sublocation = res.data.data.find(l => l._id === subLocationId);

      if (!sublocation) {
        showErrorToast('Sublocation not found');
        navigate(`/wayfinder-main/${userId}`);
        return;
      }

      // Map sublocation data to item state
      const sublocationData = {
        name: sublocation.name || '',
        buttonText: sublocation.buttonText || '',
        description: sublocation.description || '',
        status: sublocation.status ?? true,
        enabled: sublocation.enabled ?? true,
        websiteUrl: sublocation.websiteUrl || '',
        airportUrl: sublocation.airportUrl || '',
        textDirections: sublocation.textDirections || '',
        avatar_key_id: sublocation.avatar_key_id || '',
        avatar_api_key_id: sublocation.avatar_api_key_id || '',

        avatarVideo: {
          fileUrl: sublocation.avatarVideo?.fileUrl || '',
          fileName: sublocation.avatarVideo?.fileName || '',
          enabled: sublocation.avatarVideo?.enabled || false,
        },

        qrCode: {
          fileUrl: sublocation.qrCode?.fileUrl || '',
          fileName: sublocation.qrCode?.fileName || '',
          enabled: sublocation.qrCode?.enabled || false,
          width: sublocation.qrCode?.width || 100,
          height: sublocation.qrCode?.height || 100,
        },

        operationHours: sublocation.operationHours || item.operationHours,

        globalSettings: {
          ...item.globalSettings,
          ...sublocation.globalSettings,
          shadow: {
            ...item.globalSettings.shadow,
            ...sublocation.globalSettings?.shadow,
          },
          highlight: {
            ...item.globalSettings.highlight,
            ...sublocation.globalSettings?.highlight,
          },
        },

        customSettings: {
          ...item.customSettings,
          ...sublocation.customSettings,
          shadow: {
            ...item.customSettings.shadow,
            ...sublocation.customSettings?.shadow,
          },
          highlight: {
            ...item.customSettings.highlight,
            ...sublocation.customSettings?.highlight,
          },
        },

        uploadedUrls: {
          upperLeft: { ...item.uploadedUrls.upperLeft, ...sublocation.uploadedUrls?.upperLeft },
          upperRight: { ...item.uploadedUrls.upperRight, ...sublocation.uploadedUrls?.upperRight },
          bottomLeft: { ...item.uploadedUrls.bottomLeft, ...sublocation.uploadedUrls?.bottomLeft },
          bottomRight: {
            ...item.uploadedUrls.bottomRight,
            ...sublocation.uploadedUrls?.bottomRight,
          },
          backButton: { ...item.uploadedUrls.backButton, ...sublocation.uploadedUrls?.backButton },
          locationPhoto: {
            ...item.uploadedUrls.locationPhoto,
            ...sublocation.uploadedUrls?.locationPhoto,
          },
        },

        parentCategories: sublocation.parentCategories || [],
        parentSubcategories: sublocation.parentSubcategories || [],
        parentLocationId: sublocation.parentLocationId || null,
      };

      setItem(sublocationData);
    } catch (err) {
      console.error('Failed to load sublocation:', err);
      showErrorToast('Failed to load sublocation data');
    } finally {
      setLoading(false);
    }
  };

  const resolvedButtonSettings = useMemo(() => {
    return item.enabled ? item.customSettings : item.globalSettings;
  }, [item.globalSettings, item.customSettings, item.enabled]);

  // Dropzone configurations
  const { getRootProps: getPhotoRootProps, getInputProps: getPhotoInputProps } = useDropzone({
    accept: { 'image/*': [] },
    maxFiles: 1,
    onDrop: ([file]) => setPhotoFile(file),
  });

  const { getRootProps: getAvatarVideoRootProps, getInputProps: getAvatarVideoInputProps } =
    useDropzone({
      accept: { 'video/*': [] },
      maxFiles: 1,
      onDrop: ([file]) => setAvatarVideoFile(file),
    });

  const { getRootProps: getMapVideoRootProps, getInputProps: getMapVideoInputProps } =
    useDropzone({
      accept: { 'video/*': [] },
      maxFiles: 1,
      onDrop: ([file]) => setMapVideoFile(file),
    });

  const { getRootProps: getLogoBeforeMapRootProps, getInputProps: getLogoBeforeMapInputProps } =
    useDropzone({
      accept: { 'image/*': [] },
      maxFiles: 1,
      onDrop: ([file]) => setLogoBeforeMapFile(file),
    });

  const { getRootProps: getQrCodeRootProps, getInputProps: getQrCodeInputProps } = useDropzone({
    accept: { 'image/*': [] },
    maxFiles: 1,
    onDrop: ([file]) => setQrCodeFile(file),
  });

  const { getRootProps: getPngRootProps, getInputProps: getPngInputProps } = useDropzone({
    accept: { 'image/png': [] },
    maxFiles: 1,
    onDrop: ([file]) => setPngFile(file),
  });

  const { getRootProps: getClickSoundRootProps, getInputProps: getClickSoundInputProps } =
    useDropzone({
      accept: { 'audio/*': [] },
      maxFiles: 1,
      onDrop: ([file]) => setClickSoundFile(file),
    });

  const useCornerLogoDropzone = setter => {
    return useDropzone({
      accept: { 'image/*': [] },
      maxFiles: 1,
      onDrop: ([file]) => setter(file),
    });
  };

  const upperLeftDropzone = useCornerLogoDropzone(setUpperLeftLogoFile);
  const upperRightDropzone = useCornerLogoDropzone(setUpperRightLogoFile);
  const bottomLeftDropzone = useCornerLogoDropzone(setBottomLeftLogoFile);
  const bottomRightDropzone = useCornerLogoDropzone(setBottomRightLogoFile);
  const backButtonDropzone = useCornerLogoDropzone(setBackButtonFile);

  // Upload handlers
  const handleUploadMedia = async (file, position) => {
    if (!file) {
      showErrorToast(`No file selected for ${position}`);
      return;
    }

    try {
      setUploadingStates(prev => ({ ...prev, [position]: true }));
      const fileUrl = await uploadToS3(file);

      setItem(prev => ({
        ...prev,
        uploadedUrls: {
          ...prev.uploadedUrls,
          [position]: {
            ...prev.uploadedUrls[position],
            fileUrl,
            fileName: file.name,
            enabled: true,
          },
        },
      }));

      showSuccessToast(`${position} uploaded successfully`);
    } catch (error) {
      showErrorToast(`Failed to upload ${position}`);
    } finally {
      setUploadingStates(prev => ({ ...prev, [position]: false }));
    }
  };

  const handleDeleteMedia = async position => {
    if (!window.confirm(`Delete ${position}?`)) return;

    try {
      setLoadingMedia(true);
      setItem(prev => ({
        ...prev,
        uploadedUrls: {
          ...prev.uploadedUrls,
          [position]: { fileUrl: '', fileName: '', enabled: false, width: 100, height: 100 },
        },
      }));
      showSuccessToast(`${position} deleted successfully`);
    } catch (error) {
      showErrorToast(`Failed to delete ${position}`);
    } finally {
      setLoadingMedia(false);
    }
  };

  const handleUploadPhoto = async () => await handleUploadMedia(photoFile, 'locationPhoto');
  const handleUploadBackButton = async () => await handleUploadMedia(backButtonFile, 'backButton');

  const handleUploadAvatarVideo = async () => {
    if (!avatarVideoFile) {
      showErrorToast('No avatar video file selected');
      return;
    }

    try {
      setLoading(true);
      const fileUrl = await uploadToS3(avatarVideoFile);
      setItem(prev => ({
        ...prev,
        avatarVideo: {
          ...prev.avatarVideo,
          fileUrl,
          fileName: avatarVideoFile.name,
          enabled: true,
        },
      }));
      setAvatarVideoFile(null);
      showSuccessToast('Avatar video uploaded successfully');
    } catch (error) {
      showErrorToast('Failed to upload avatar video');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadMapVideo = async () => {
    if (!mapVideoFile) {
      showErrorToast('No map video file selected');
      return;
    }

    try {
      setLoading(true);
      const fileUrl = await uploadToS3(mapVideoFile);
      setItem(prev => ({
        ...prev,
        mapVideo: {
          ...prev.mapVideo,
          fileUrl,
          fileName: mapVideoFile.name,
          enabled: true,
        },
      }));
      setMapVideoFile(null);
      showSuccessToast('Map video uploaded successfully');
    } catch (error) {
      showErrorToast('Failed to upload map video');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadLogoBeforeMap = async () => {
    if (!logoBeforeMapFile) {
      showErrorToast('No logo file selected');
      return;
    }

    try {
      setLoading(true);
      const fileUrl = await uploadToS3(logoBeforeMapFile);
      setItem(prev => ({
        ...prev,
        logoBeforeMap: {
          ...prev.logoBeforeMap,
          fileUrl,
          fileName: logoBeforeMapFile.name,
          enabled: true,
        },
      }));
      setLogoBeforeMapFile(null);
      showSuccessToast('Logo before map uploaded successfully');
    } catch (error) {
      showErrorToast('Failed to upload logo before map');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadQrCode = async () => {
    if (!qrCodeFile) {
      showErrorToast('No QR code file selected');
      return;
    }

    try {
      setLoading(true);
      const fileUrl = await uploadToS3(qrCodeFile);
      setItem(prev => ({
        ...prev,
        qrCode: { ...prev.qrCode, fileUrl, fileName: qrCodeFile.name, enabled: true },
      }));
      setQrCodeFile(null);
      showSuccessToast('QR code uploaded successfully');
    } catch (error) {
      showErrorToast('Failed to upload QR code');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadPng = async () => {
    if (!pngFile) {
      showErrorToast('No PNG file selected');
      return;
    }

    try {
      setLoading(true);
      const fileUrl = await uploadToS3(pngFile);
      const settingsPath = item.enabled ? 'customSettings' : 'globalSettings';
      setItem(prev => {
        const existingBtnPng = prev[settingsPath]?.btnPng;
        const existingBtnPngFileName = prev[settingsPath]?.btnPngFileName;

        const updatedBtnPng = typeof existingBtnPng === 'object' && existingBtnPng !== null
          ? { ...existingBtnPng, [currentLanguage]: fileUrl }
          : { en: existingBtnPng || fileUrl, [currentLanguage]: fileUrl };

        const updatedBtnPngFileName = typeof existingBtnPngFileName === 'object' && existingBtnPngFileName !== null
          ? { ...existingBtnPngFileName, [currentLanguage]: pngFile.name }
          : { en: existingBtnPngFileName || pngFile.name, [currentLanguage]: pngFile.name };

        return {
          ...prev,
          [settingsPath]: {
            ...prev[settingsPath],
            btnPng: updatedBtnPng,
            btnPngFileName: updatedBtnPngFileName,
            pngBtnenabled: true,
          },
        };
      });
      setPngFile(null);
      showSuccessToast('Button PNG uploaded successfully');
    } catch (error) {
      showErrorToast('Failed to upload PNG');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadClickSound = async () => {
    if (!clickSoundFile) {
      showErrorToast('No click sound file selected');
      return;
    }

    try {
      setLoading(true);
      const fileUrl = await uploadToS3(clickSoundFile);
      const settingsPath = item.enabled ? 'customSettings' : 'globalSettings';
      setItem(prev => ({
        ...prev,
        [settingsPath]: {
          ...prev[settingsPath],
          clickSound: fileUrl,
          clickSoundFilename: clickSoundFile.name,
        },
      }));
      setClickSoundFile(null);
      showSuccessToast('Click sound uploaded successfully');
    } catch (error) {
      showErrorToast('Failed to upload click sound');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadIcon = async () => {
    if (!btnIcon) {
      showErrorToast('No icon file selected');
      return;
    }

    try {
      setLoading(true);
      const fileUrl = await uploadToS3(btnIcon);
      const settingsPath = item.enabled ? 'customSettings' : 'globalSettings';
      setItem(prev => ({
        ...prev,
        [settingsPath]: {
          ...prev[settingsPath],
          buttonIcon: fileUrl,
          buttonIconFileName: btnIcon.name,
          enableIcon: true,
        },
      }));
      setBtnIcon(null);
      showSuccessToast('Button icon uploaded successfully');
    } catch (error) {
      showErrorToast('Failed to upload button icon');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadCornerLogo = async (corner, file, setter) => {
    if (!file) {
      showErrorToast(`No ${corner} logo file selected`);
      return;
    }

    try {
      setLoading(true);
      const fileUrl = await uploadToS3(file);
      setItem(prev => ({
        ...prev,
        uploadedUrls: {
          ...prev.uploadedUrls,
          [corner]: {
            ...prev.uploadedUrls[corner],
            fileUrl,
            fileName: file.name,
            enabled: true,
          },
        },
      }));
      setter(null);
      showSuccessToast(`${corner} logo uploaded successfully`);
    } catch (error) {
      showErrorToast(`Failed to upload ${corner} logo`);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async e => {
    e.preventDefault();

    try {
      setLoading(true);
      const saveData = {
        ...item,
        userId,
        globalSettings: item.globalSettings || {},
        customSettings: item.customSettings || {},
      };

      const res = await axios.put(`/api/wayfinder/sublocations/${subLocationId}`, saveData);

      if (res.data.success) {
        showSuccessToast('Sublocation saved successfully');
        fetchSubLocationData();
      } else {
        showErrorToast(res.data.message || 'Save failed');
      }
    } catch (error) {
      console.error('Save error:', error);
      showErrorToast(error.response?.data?.message || 'Save failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this sublocation permanently?')) return;

    try {
      await axios.delete(`/api/wayfinder/sublocations/${subLocationId}?userId=${userId}`);
      showSuccessToast('Sublocation deleted successfully');
      navigate(`/wayfinder-main/${userId}`);
    } catch (error) {
      showErrorToast('Delete failed');
    }
  };

  const handleReset = () => {
    fetchSubLocationData();
    setPhotoFile(null);
    setAvatarVideoFile(null);
    setMapVideoFile(null);
    setLogoBeforeMapFile(null);
    setQrCodeFile(null);
    setPngFile(null);
    setClickSoundFile(null);
    setBtnIcon(null);
    setUpperLeftLogoFile(null);
    setUpperRightLogoFile(null);
    setBottomLeftLogoFile(null);
    setBottomRightLogoFile(null);
    setBackButtonFile(null);
  };

  const handleCornerLogoChange = (corner, field, value) => {
    setItem(prev => ({
      ...prev,
      uploadedUrls: {
        ...prev.uploadedUrls,
        [corner]: { ...prev.uploadedUrls[corner], [field]: value },
      },
    }));
  };

  const handleCornerLogoToggle = (corner, enabled) => {
    setItem(prev => ({
      ...prev,
      uploadedUrls: {
        ...prev.uploadedUrls,
        [corner]: { ...prev.uploadedUrls[corner], enabled },
      },
    }));
  };

  const handleItemChange = (field, value) => {
    const keys = field.split('.');
    setItem(prev => {
      const updatedItem = { ...prev };
      let current = updatedItem;

      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) {
          current[keys[i]] = {};
        } else {
          current[keys[i]] = { ...current[keys[i]] };
        }
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = value;
      return updatedItem;
    });
  };

  const handleSettingsChange = (key, value) => {
    const settingsPath = item.enabled ? 'customSettings' : 'globalSettings';
    handleItemChange(`${settingsPath}.${key}`, value);
  };

  const toggleLocationEnabled = () => {
    setItem(prev => ({ ...prev, enabled: !prev.enabled }));
  };

  return (
    <WayfinderLocationTemplate
      type="sublocation"
      userId={userId}
      locationId={subLocationId}
      item={item}
      setItem={setItem}
      resolvedButtonSettings={resolvedButtonSettings}
      loading={loading || s3Uploading}
      buttonColor={buttonColor}
      photoFile={photoFile}
      avatarVideoFile={avatarVideoFile}
      mapVideoFile={mapVideoFile}
      logoBeforeMapFile={logoBeforeMapFile}
      qrCodeFile={qrCodeFile}
      pngFile={pngFile}
      clickSoundFile={clickSoundFile}
      backButtonFile={backButtonFile}
      upperLeftLogoFile={upperLeftLogoFile}
      upperRightLogoFile={upperRightLogoFile}
      bottomLeftLogoFile={bottomLeftLogoFile}
      bottomRightLogoFile={bottomRightLogoFile}
      getPhotoRootProps={getPhotoRootProps}
      getPhotoInputProps={getPhotoInputProps}
      getAvatarVideoRootProps={getAvatarVideoRootProps}
      getAvatarVideoInputProps={getAvatarVideoInputProps}
      getQrCodeRootProps={getQrCodeRootProps}
      getQrCodeInputProps={getQrCodeInputProps}
      getPngRootProps={getPngRootProps}
      getPngInputProps={getPngInputProps}
      getClickSoundRootProps={getClickSoundRootProps}
      getClickSoundInputProps={getClickSoundInputProps}
      getUpperLeftLogoRootProps={upperLeftDropzone.getRootProps}
      getUpperLeftLogoInputProps={upperLeftDropzone.getInputProps}
      getUpperRightLogoRootProps={upperRightDropzone.getRootProps}
      getUpperRightLogoInputProps={upperRightDropzone.getInputProps}
      getBottomLeftLogoRootProps={bottomLeftDropzone.getRootProps}
      getBottomLeftLogoInputProps={bottomLeftDropzone.getInputProps}
      getBottomRightLogoRootProps={bottomRightDropzone.getRootProps}
      getBottomRightLogoInputProps={bottomRightDropzone.getInputProps}
      getBackButtonRootProps={backButtonDropzone.getRootProps}
      getBackButtonInputProps={backButtonDropzone.getInputProps}
      handleUploadPhoto={handleUploadPhoto}
      handleUploadBackButton={handleUploadBackButton}
      handleUploadAvatarVideo={handleUploadAvatarVideo}
      handleUploadMapVideo={handleUploadMapVideo}
      handleUploadLogoBeforeMap={handleUploadLogoBeforeMap}
      handleUploadQrCode={handleUploadQrCode}
      handleUploadPng={handleUploadPng}
      handleUploadClickSound={handleUploadClickSound}
      btnIcon={btnIcon}
      setBtnIcon={setBtnIcon}
      handleUploadIcon={handleUploadIcon}
      handleUploadUpperLeftLogo={() =>
        handleUploadCornerLogo('upperLeft', upperLeftLogoFile, setUpperLeftLogoFile)
      }
      handleUploadUpperRightLogo={() =>
        handleUploadCornerLogo('upperRight', upperRightLogoFile, setUpperRightLogoFile)
      }
      handleUploadBottomLeftLogo={() =>
        handleUploadCornerLogo('bottomLeft', bottomLeftLogoFile, setBottomLeftLogoFile)
      }
      handleUploadBottomRightLogo={() =>
        handleUploadCornerLogo('bottomRight', bottomRightLogoFile, setBottomRightLogoFile)
      }
      handleUploadMedia={handleUploadMedia}
      handleDeleteMedia={handleDeleteMedia}
      loadingMedia={loadingMedia}
      uploadingStates={uploadingStates}
      setUploadingStates={setUploadingStates}
      handleSave={handleSave}
      handleDelete={handleDelete}
      handleReset={handleReset}
      handleItemChange={handleItemChange}
      handleSettingsChange={handleSettingsChange}
      handleCornerLogoChange={handleCornerLogoChange}
      handleCornerLogoToggle={handleCornerLogoToggle}
      toggleLocationEnabled={toggleLocationEnabled}
      setPhotoFile={setPhotoFile}
      setAvatarVideoFile={setAvatarVideoFile}
      setMapVideoFile={setMapVideoFile}
      setLogoBeforeMapFile={setLogoBeforeMapFile}
      setQrCodeFile={setQrCodeFile}
      setPngFile={setPngFile}
      setClickSoundFile={setClickSoundFile}
      setUpperLeftLogoFile={setUpperLeftLogoFile}
      setUpperRightLogoFile={setUpperRightLogoFile}
      setBottomLeftLogoFile={setBottomLeftLogoFile}
      setBottomRightLogoFile={setBottomRightLogoFile}
      setBackButtonFile={setBackButtonFile}
      fetchLocationData={fetchSubLocationData}
    />
  );
};

export default WayfinderSubLocation;
